import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { DashboardLayout } from '@/components/templates/DashboardLayout';
import { useAuthStore } from '@/stores/useAuthStore';
import { ProtectedRoute, PublicOnlyRoute, RoleRoute } from './ProtectedRoute';
import { paths, homePath } from './paths';
import { AdminTicketIdRedirect, CedoliniPortalEntry, RedirectAdminTask, RedirectAggregatiReport, RedirectPreserveSearch, UtentiEntry } from './redirects';
import {
  ADMINISTRATIVE_STAFF_ROLES,
  COMPANY_HEALTH_ROLES,
  STAFF_ROLES,
  TECHNICAL_STAFF_ROLES,
  isStaffRole,
} from '@/types';
import type { Ruolo } from '@/types';

/**
 * Route table.
 *
 * Every page is lazily imported so each becomes its own chunk: a tenant user
 * never downloads the admin CRUD screens, and the dashboard's first paint is
 * not blocked by Recharts (only the report pages pull it in).
 *
 * The public home is the unauthenticated entry point. Login stays eager so the
 * sign-in form paints without a chunk round-trip.
 */
import { LoginPage } from '@/pages/auth/LoginPage';

const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const RegistrazionePage = lazy(() => import('@/pages/auth/RegistrazionePage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const ServiziPage = lazy(() => import('@/pages/servizi/ServiziPage'));
const MacchinaDetailPage = lazy(() => import('@/pages/servizi/MacchinaDetailPage'));
const BackupPage = lazy(() => import('@/pages/servizi/BackupPage'));
const BackupJobDetailPage = lazy(() => import('@/pages/servizi/BackupJobDetailPage'));
const ModuloEventiPage = lazy(() => import('@/pages/servizi/ModuloEventiPage'));

const TicketListPage = lazy(() => import('@/pages/ticket/TicketListPage'));
const TicketCreatePage = lazy(() => import('@/pages/ticket/TicketCreatePage'));
const TicketOpenPage = lazy(() => import('@/pages/ticket/TicketOpenPage'));
const TicketClosedPage = lazy(() => import('@/pages/ticket/TicketClosedPage'));
const TicketDetailPage = lazy(() => import('@/pages/ticket/TicketDetailPage'));

const ReportPage = lazy(() => import('@/pages/report/ReportPage'));
const SicurezzaPage = lazy(() => import('@/pages/sicurezza/SicurezzaPage'));
const StaffSicurezzaPage = lazy(() => import('@/pages/admin/sicurezza/StaffSicurezzaPage'));

const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminOperativaPage = lazy(() => import('@/pages/admin/AdminOperativaPage'));
const ClientiPage = lazy(() => import('@/pages/admin/ClientiPage'));
const LeadsPage = lazy(() => import('@/pages/admin/LeadsPage'));
const LeadImportPage = lazy(() => import('@/pages/admin/LeadImportPage'));
const LeadCalendarioPage = lazy(() => import('@/pages/admin/LeadCalendarioPage'));
const LeadDetailPage = lazy(() => import('@/pages/admin/LeadDetailPage'));
const ClientiPersiPage = lazy(() => import('@/pages/admin/ClientiPersiPage'));
const ClienteDetailPage = lazy(() => import('@/pages/admin/ClienteDetailPage'));
const ContrattiPage = lazy(() => import('@/pages/admin/ContrattiPage'));
const AdminContrattoDetailPage = lazy(() => import('@/pages/admin/AdminContrattoDetailPage'));
const AdminProdottiPage = lazy(() => import('@/pages/admin/AdminProdottiPage'));
const AdminServiziCatalogPage = lazy(() => import('@/pages/admin/AdminServiziCatalogPage'));
const AdminMacchinePage = lazy(() => import('@/pages/admin/AdminMacchinePage'));
const MachineAlertsPage = lazy(() => import('@/pages/admin/MachineAlertsPage'));
const StatoSalutePage = lazy(() => import('@/pages/admin/StatoSalutePage'));
const BackupPendingPage = lazy(() => import('@/pages/admin/BackupPendingPage'));
const CodaTicketPage = lazy(() => import('@/pages/admin/CodaTicketPage'));
const TasksPage = lazy(() => import('@/pages/admin/TasksPage'));
const TaskDetailPage = lazy(() => import('@/pages/admin/TaskDetailPage'));
const AdminCategoriePage = lazy(() => import('@/pages/admin/AdminCategoriePage'));
const CedoliniListPage = lazy(() => import('@/pages/cedolini/CedoliniListPage'));
const FattureDemoPage = lazy(() => import('@/pages/admin/FattureDemoPage'));
const CedolinoDetailPage = lazy(() => import('@/pages/cedolini/CedolinoDetailPage'));
const PreventiviListPage = lazy(() => import('@/pages/admin/PreventiviListPage'));
const PreventiviMensiliPage = lazy(() => import('@/pages/admin/PreventiviMensiliPage'));
const PreventivoWizardPage = lazy(() => import('@/pages/admin/PreventivoWizardPage'));
const PreventivoDetailPage = lazy(() => import('@/pages/admin/PreventivoDetailPage'));
const PreventivoImpostazioniPage = lazy(() => import('@/pages/admin/PreventivoImpostazioniPage'));
const PortalPreventiviPage = lazy(() => import('@/pages/preventivi/PortalPreventiviPage'));
const PortalPreventivoDetailPage = lazy(() => import('@/pages/preventivi/PortalPreventivoDetailPage'));
const DocumentiPage = lazy(() => import('@/pages/documenti/DocumentiPage'));
const DocumentazioneInternaPage = lazy(() => import('@/pages/documenti/DocumentazioneInternaPage'));
const DocumentazioneClientePage = lazy(() => import('@/pages/documenti/DocumentazioneClientePage'));
const DocumentoDetailPage = lazy(() => import('@/pages/documenti/DocumentoDetailPage'));
const VisibilitaTogglePanel = lazy(() => import('@/pages/documenti/VisibilitaTogglePanel'));
const PortalContrattiPage = lazy(() => import('@/pages/contratti/PortalContrattiPage'));
const PortalContrattoDetailPage = lazy(() => import('@/pages/contratti/PortalContrattoDetailPage'));
const PortalContrattoFirmaPage = lazy(() => import('@/pages/contratti/PortalContrattoFirmaPage'));
const PortalOrganizzazionePage = lazy(() => import('@/pages/organizzazione/PortalOrganizzazionePage'));

const UtentiPage = lazy(() => import('@/pages/utenti/UtentiPage'));
const ImpostazioniPage = lazy(() => import('@/pages/ImpostazioniPage'));
const VaultwardenAdminPage = lazy(() => import('@/pages/admin/VaultwardenAdminPage'));
const FrameworkMatrixPage = lazy(() => import('@/pages/admin/framework-matrix/FrameworkMatrixPage'));
const NotifichePage = lazy(() => import('@/pages/NotifichePage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/ForbiddenPage'));

const TENANT_MANAGERS: readonly Ruolo[] = ['cliente_admin', ...STAFF_ROLES];

/** `cliente_user` lands on in-progress tickets; billing staff on cedolini; technical staff on the operational dashboard. */
/** Cedolini, preventivi and contratti stay on the partner. Cliente finale users get 403. */
function PricesOnly({ children }: { children: JSX.Element }): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const allowed = user != null && (isStaffRole(user.ruolo) || user.cliente_tipo !== 'cliente_finale');
  if (!allowed) return <Navigate to={paths.forbidden} replace />;
  return children;
}

function HomeIndex(): JSX.Element {
  const ruolo = useAuthStore((state) => state.user?.ruolo ?? null);
  if (isStaffRole(ruolo) || ruolo === 'cliente_user') {
    return <Navigate to={homePath(ruolo)} replace />;
  }
  return <DashboardPage />;
}

/** Company operators see their in-progress tickets — the full inbox stays for admins and staff. */
function TicketIndex(): JSX.Element {
  const ruolo = useAuthStore((state) => state.user?.ruolo ?? null);
  if (ruolo === 'cliente_user') {
    return <Navigate to={paths.ticket.inLavorazione} replace />;
  }
  return <TicketListPage />;
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route
        path={paths.login}
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />

      <Route
        path={paths.forgotPassword}
        element={
          <Suspense fallback={null}>
            <ForgotPasswordPage />
          </Suspense>
        }
      />

      <Route
        path={paths.registrazione}
        element={
          <PublicOnlyRoute>
            <Suspense fallback={null}>
              <RegistrazionePage />
            </Suspense>
          </PublicOnlyRoute>
        }
      />

      <Route
        path={paths.verifyEmail}
        element={
          <Suspense fallback={null}>
            <VerifyEmailPage />
          </Suspense>
        }
      />

      <Route
        path={paths.resetPassword}
        element={
          <Suspense fallback={null}>
            <ResetPasswordPage />
          </Suspense>
        }
      />

      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          <Route index element={<HomeIndex />} />
          <Route path="calendario" element={<LeadCalendarioPage />} />

          <Route path="servizi" element={<RoleRoute roles={COMPANY_HEALTH_ROLES} />}>
            <Route index element={<ServiziPage />} />
            <Route path="macchine" element={<Navigate to={`${paths.servizi.root}?tab=macchine`} replace />} />
            <Route path="macchine/:macchinaId" element={<MacchinaDetailPage />} />
            <Route path="backup" element={<BackupPage />} />
            <Route path="backup/:jobId" element={<BackupJobDetailPage />} />
            {/* One route for firewall / antivirus / syslog: they read the same
                `service_events` table and differ only by the `modulo` filter. */}
            <Route path=":modulo" element={<ModuloEventiPage />} />
          </Route>

          <Route path="ticket">
            <Route index element={<TicketIndex />} />
            <Route path="nuovo" element={<TicketCreatePage />} />
            <Route path="in-lavorazione" element={<TicketOpenPage />} />
            <Route path="chiusi" element={<TicketClosedPage />} />
            <Route path=":ticketId" element={<TicketDetailPage />} />
          </Route>

          <Route
            path="report"
            element={
              <RoleRoute roles={['cliente_admin', ...STAFF_ROLES]}>
                <ReportPage />
              </RoleRoute>
            }
          />

          <Route
            path="sicurezza"
            element={
              <RoleRoute roles={[...STAFF_ROLES]}>
                <SicurezzaPage />
              </RoleRoute>
            }
          />

          <Route path="cedolini" element={<CedoliniPortalEntry />}>
            <Route index element={<PricesOnly><CedoliniListPage variant="portal" /></PricesOnly>} />
            <Route path=":cedolinoId" element={<PricesOnly><CedolinoDetailPage variant="portal" /></PricesOnly>} />
          </Route>

          <Route path="preventivi" element={<RoleRoute roles={['cliente_admin']} />}>
            <Route index element={<PortalPreventiviPage />} />
            <Route path=":preventivoId" element={<PortalPreventivoDetailPage />} />
          </Route>

          <Route path="contratti" element={<RoleRoute roles={['cliente_admin']} />}>
            <Route index element={<PricesOnly><PortalContrattiPage /></PricesOnly>} />
            <Route path=":contrattoId/firma" element={<PricesOnly><PortalContrattoFirmaPage /></PricesOnly>} />
            <Route path=":contrattoId" element={<PricesOnly><PortalContrattoDetailPage /></PricesOnly>} />
          </Route>

          <Route
            path="organizzazione"
            element={
              <RoleRoute roles={['cliente_admin']}>
                <PortalOrganizzazionePage />
              </RoleRoute>
            }
          />

          <Route path="documenti">
            <Route index element={<DocumentiPage variant="portal" />} />
            <Route
              path="visibilita"
              element={
                <RoleRoute roles={['cliente_admin']}>
                  <VisibilitaTogglePanel />
                </RoleRoute>
              }
            />
            <Route path=":documentoId" element={<DocumentoDetailPage variant="portal" />} />
          </Route>

          <Route path="admin" element={<RoleRoute roles={STAFF_ROLES} />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="operativa" element={<AdminOperativaPage />} />
            <Route path="sicurezza" element={<StaffSicurezzaPage />} />
            <Route path="clienti-persi" element={<ClientiPersiPage />} />
            <Route path="clienti" element={<ClientiPage />} />
            <Route path="clienti/:clienteId" element={<ClienteDetailPage />} />
            <Route path="lead" element={<LeadsPage />} />
            <Route path="calendario" element={<Navigate to={paths.calendario} replace />} />
            <Route path="lead/calendario" element={<Navigate to={paths.admin.leadCalendario} replace />} />
            <Route path="lead/import" element={<LeadImportPage />} />
            <Route path="lead/:leadId" element={<LeadDetailPage />} />
            <Route
              path="framework-matrix"
              element={
                <RoleRoute roles={[...ADMINISTRATIVE_STAFF_ROLES]}>
                  <FrameworkMatrixPage />
                </RoleRoute>
              }
            />
            <Route path="contratti" element={<ContrattiPage />} />
            <Route path="contratti/:contrattoId" element={<AdminContrattoDetailPage />} />
            <Route path="catalogo/prodotti" element={<AdminProdottiPage />} />
            <Route path="catalogo/servizi" element={<AdminServiziCatalogPage />} />
            <Route path="prodotti" element={<RedirectPreserveSearch to={paths.admin.catalog.products} />} />
            <Route path="servizi" element={<RedirectPreserveSearch to={paths.admin.catalog.services} />} />
            <Route path="cedolini/nuovo" element={<Navigate to={paths.admin.cedolini} replace />} />
            <Route path="cedolini" element={<CedoliniListPage variant="admin" />} />
            <Route path="cedolini/:cedolinoId" element={<CedolinoDetailPage variant="admin" />} />
            <Route path="fatture" element={<FattureDemoPage />} />
            <Route path="preventivi" element={<PreventiviListPage />} />
            <Route path="preventivi/mensili" element={<PreventiviMensiliPage />} />
            <Route path="preventivi/nuovo" element={<PreventivoWizardPage />} />
            <Route path="preventivi/impostazioni" element={<PreventivoImpostazioniPage />} />
            <Route path="preventivi/:preventivoId" element={<PreventivoDetailPage />} />
            <Route
              path="documenti/visibilita"
              element={
                <RoleRoute roles={['admin']}>
                  <VisibilitaTogglePanel />
                </RoleRoute>
              }
            />
            <Route path="documenti/interna" element={<DocumentazioneInternaPage />} />
            <Route path="documenti/interna/:clienteId" element={<DocumentazioneClientePage />} />
            <Route path="documenti" element={<DocumentiPage variant="admin" />} />
            <Route path="documenti/:documentoId" element={<DocumentoDetailPage variant="admin" />} />
            <Route path="macchine" element={<RoleRoute roles={TECHNICAL_STAFF_ROLES}><AdminMacchinePage /></RoleRoute>} />
            <Route path="macchine/stato-di-salute" element={<StatoSalutePage />} />
            <Route path="macchine-in-allarme" element={<RedirectPreserveSearch to={paths.admin.backup.alert} />} />
            <Route path="backup" element={<RoleRoute roles={TECHNICAL_STAFF_ROLES}><BackupPage /></RoleRoute>} />
            <Route path="backup/alert" element={<MachineAlertsPage />} />
            <Route path="backup/pending" element={<BackupPendingPage />} />
            <Route path="backup-pending" element={<RedirectPreserveSearch to={paths.admin.backup.pending} />} />
            <Route
              path="ticket"
              element={
                <RoleRoute roles={TECHNICAL_STAFF_ROLES}>
                  <AdminTicketIdRedirect>
                    <CodaTicketPage />
                  </AdminTicketIdRedirect>
                </RoleRoute>
              }
            />
            <Route path="attivita" element={<TasksPage />} />
            <Route path="attivita/:taskId" element={<TaskDetailPage />} />
            <Route path="tasks/:taskId" element={<RedirectAdminTask />} />
            <Route path="tasks" element={<Navigate to={paths.admin.activities.list} replace />} />
            <Route path="categorie" element={<RoleRoute roles={['admin']}><AdminCategoriePage /></RoleRoute>} />
            <Route path="report" element={<RedirectAggregatiReport />} />
            <Route path="utenti" element={<UtentiPage />} />
          </Route>

          <Route
            path="utenti"
            element={
              <RoleRoute roles={TENANT_MANAGERS}>
                <UtentiEntry>
                  <UtentiPage />
                </UtentiEntry>
              </RoleRoute>
            }
          />

          <Route path="impostazioni" element={<ImpostazioniPage />} />
          <Route
            path="impostazioni/vaultwarden"
            element={
              <RoleRoute roles={['admin']}>
                <VaultwardenAdminPage />
              </RoleRoute>
            }
          />
          <Route path="notifiche" element={<NotifichePage />} />

          <Route path="403" element={<ForbiddenPage />} />
          <Route path="404" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to={paths.notFound} replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
